import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import PasswordInput from '../components/ui/PasswordInput';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, KeyRound } from 'lucide-react';
import api from '../services/api';

// Change password for a signed-in user.
//   - Requires current password (re-auth)
//   - New password must meet PasswordValidator rules (12+ chars, mixed classes,
//     no sequential runs, not in weak list) — server enforces, error string
//     shows verbatim in the toast
//   - On success the backend revokes all tokens for this user → we log out
//     locally and bounce to /login so the new credentials get a fresh JWT.

const ChangePassword = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();

  const [current, setCurrent]       = useState('');
  const [next, setNext]             = useState('');
  const [confirmNext, setConfirmNext] = useState('');
  const [loading, setLoading]       = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!current)                  { toast.error('Enter your current password.');             return; }
    if (!next)                     { toast.error('Enter a new password.');                    return; }
    if (next !== confirmNext)      { toast.error('New passwords do not match.');              return; }
    if (next === current)          { toast.error('New password must differ from current.');   return; }

    setLoading(true);
    try {
      await api.post('/auth/change-password', {
        current_password: current,
        new_password:     next,
      });
      toast.success('Password changed. Please sign in again.');
      // Backend revokes all tokens for this user → existing JWT is now invalid.
      // Clear local session so the next request doesn't 401 in the background.
      logout();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not change password.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 p-3 md:p-4">
      <div className="w-full max-w-xl mx-auto space-y-4">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <h1 className="text-2xl font-bold text-slate-900">Change Password</h1>
        </div>

        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4 text-slate-600">
            <KeyRound className="w-4 h-4" />
            <span className="text-sm">
              You'll be signed out of all sessions after a successful change.
            </span>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="cp-current">Current Password</Label>
              <PasswordInput
                id="cp-current"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                placeholder="••••••••"
                autoFocus
                required
              />
            </div>

            <div>
              <Label htmlFor="cp-new">New Password</Label>
              <PasswordInput
                id="cp-new"
                value={next}
                onChange={(e) => setNext(e.target.value)}
                placeholder="••••••••"
                required
              />
              <p className="text-xs text-slate-400 mt-1">
                At least 12 characters with upper, lower, digit, and special.
                Cannot reuse the current password.
              </p>
            </div>

            <div>
              <Label htmlFor="cp-confirm">Confirm New Password</Label>
              <PasswordInput
                id="cp-confirm"
                value={confirmNext}
                onChange={(e) => setConfirmNext(e.target.value)}
                placeholder="••••••••"
                required
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => navigate(-1)} disabled={loading}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Updating…' : 'Change Password'}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default ChangePassword;
